<template>
  <div class="product-downloads__button">
    <template v-if="resource.items.length > 1">
      <span
        class="product-downloads__text"
        :class="{
          'product-downloads__text_active': isOpen,
          'product-downloads__text_top-direction': isTopDirection
        }"
        @click="toggleDownloadOptions"
      >
        {{ langJson[lang].download }}
        <span class="icon-arrow-expand"></span>
      </span>

      <div class="product-downloads__options" :style="{ '--downloads-dropdown-height': `${dropdownHeight}px` }">
        <div class="product-downloads__options-inner" ref="downloadOptionsInner">
          <div v-for="(item, innerIndex) in resource.items" class="product-downloads__option" :key="'file-extension-' + innerIndex">
            <a
              :href="`${baseUrlMedia}${item.fileId}/${item.fileName}${item.extension === 'dwg' ? '' : '/Download'}`"
              class="product-downloads__link"
              @click="
                setDataLayers({
                  name: item.fileName,
                  fileType: item.extension,
                  event: 'downloadFile'
                })
              "
            >
              <i class="icon-download"></i>
              {{ item.extension }}
            </a>
          </div>
          <div class="product-downloads__option">
            <a
              :href="`${baseUrlMedia}zip/?files=${prepareZipLink(resource)}&name=bundle.zip`"
              class="product-downloads__link"
              @click="
                setDataLayers({
                  name: resource.item.fileName,
                  fileType: 'zip',
                  event: 'downloadFile'
                })
              "
            >
              <i class="icon-download"></i>
              zip
            </a>
          </div>
        </div>
      </div>
    </template>

    <template v-else>
      <a
        :href="`${baseUrlMedia}${resource.item.fileId}/${resource.item.fileName}${resource.item.extension === 'dwg' ? '' : '/Download'}`"
        class="product-downloads__link"
        @click="
          setDataLayers({
            name: `${resource.item.fileName}`,
            fileType: `${resource.item.fileName.split('.').pop()}`,
            event: 'downloadFile'
          })
        "
      >
        <i class="icon-download"></i>
        {{ resource.item.extension }}
      </a>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';

  interface ResourceItem {
    fileId: string;
    fileName: string;
    extension: string;
    mimeType?: string;
  }

  interface Resource {
    item: ResourceItem;
    items: ResourceItem[];
  }

  const props = defineProps<{
    resource: Resource;
    item?: ResourceItem;
  }>();

  const mainStore = useMainStore();
  const baseUrlMedia = computed(() => mainStore.baseUrlMedia);
  const langJson = computed(() => mainStore.langJson);
  const lang = computed(() => mainStore.lang);

  const downloadOptionsInner = ref<HTMLElement | null>(null);
  const isOpen = ref(false);
  const isTopDirection = ref(false);
  const dropdownHeight = ref(0);

  const isImage = computed(() => props.item && props.item.mimeType && props.item.mimeType.indexOf('image') > -1);
  const filePathExtension = computed(() => (isImage.value ? '?width=300&height=300' : '/Preview?mimetype=image/jpg'));

  interface DataLayersData {
    name: string;
    fileType: string;
    event: string;
  }

  function setDataLayers(data: DataLayersData) {
    // Implement as needed or port from Vuex action
  }

  function prepareZipLink(resourceObj: Resource) {
    let link = '';
    resourceObj.items.forEach((element: ResourceItem, index: number) => {
      link += `${element.fileId}%2F${element.fileName}`;
      if (index !== resourceObj.items.length - 1) {
        link += '%2C';
      }
    });
    return link;
  }

  function measureDropdown() {
    if (!downloadOptionsInner.value) return;
    // The options wrapper is clipped by max-height, so the open height has to come
    // from the inner list, which keeps its natural height regardless of the clip.
    dropdownHeight.value = downloadOptionsInner.value.scrollHeight;
    isTopDirection.value = downloadOptionsInner.value.getBoundingClientRect().bottom > window.innerHeight;
  }
  function toggleDownloadOptions() {
    if (isOpen.value) return;
    isTopDirection.value = false;
    // The body listener that closes any open dropdown runs first on this same
    // click, so opening is deferred until after it has closed the others.
    setTimeout(() => {
      measureDropdown();
      isOpen.value = true;
    }, 0);
  }
  function closeDownloadOptions() {
    isOpen.value = false;
  }

  onMounted(() => {
    if (typeof window !== 'undefined') {
      document.body.addEventListener('click', closeDownloadOptions);
    }
  });
  onBeforeUnmount(() => {
    if (typeof window !== 'undefined') {
      document.body.removeEventListener('click', closeDownloadOptions);
    }
  });
</script>
